import { useState, useEffect } from "react";
import styled from "styled-components";
import { FiMapPin, FiDroplet, FiZap, FiDollarSign } from "react-icons/fi";

export default function CalculadoraCombustivel() {
  const [distance, setDistance] = useState("");
  const [fuel, setFuel] = useState("");
  const [consumption, setConsumption] = useState("");
  const [price, setPrice] = useState("");
  const [total, setTotal] = useState("0,00");

  const [distanceUnit, setDistanceUnit] = useState("km");
  const [fuelUnit, setFuelUnit] = useState("l");

  // Efeito para calcular consumo e total
  useEffect(() => {
    const d = parseFloat(distance);
    const f = parseFloat(fuel);
    const p = parseFloat(price);

    // Cálculo da Média (Distância / Combustível)
    if (d > 0 && f > 0) {
      setConsumption((d / f).toFixed(2));
    } else {
      setConsumption("");
    }

    // Cálculo do Total (Combustível * Preço)
    if (f > 0 && p > 0) {
      const res = (f * p).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      setTotal(res);
    } else {
      setTotal("0,00");
    }
  }, [distance, fuel, price]); // Removi o consumption das dependências para evitar loop

  function handleNumberInput(value, callback) {
    // Aceita apenas números e um ponto/vírgula
    let cleanValue = value.replace(/[^0-9.,]/g, "").replace(",", ".");
    callback(cleanValue);
  }

  // Função para formatar o preço ao sair do campo (onBlur)
  function formatPriceOnBlur() {
    if (price !== "") {
      const p = parseFloat(price);
      if (!isNaN(p)) {
        setPrice(p.toFixed(2));
      }
    }
  }

  const clearFields = () => {
    setDistance("");
    setFuel("");
    setConsumption("");
    setPrice("");
    setTotal("0,00");
  };

  return (
    <Container>
      <Content>
        <Header>
          <Badge>Ferramentas Automotivas</Badge>
          <h1>Calculadora de <span>Combustível</span></h1>
        </Header>

        <MainCard>
          <Grid>
            <Field>
              <label><FiMapPin /> Distância percorrida</label>
              <InputGroup>
                <input
                  type="text"
                  placeholder="0"
                  value={distance}
                  onChange={(e) => handleNumberInput(e.target.value, setDistance)}
                />
                <select onChange={(e) => setDistanceUnit(e.target.value)}>
                  <option value="km">Km</option>
                  <option value="mi">Milhas</option>
                </select>
              </InputGroup>
            </Field>

            <Field>
              <label><FiDroplet /> Combustível gasto</label>
              <InputGroup>
                <input
                  type="text"
                  placeholder="0"
                  value={fuel}
                  onChange={(e) => handleNumberInput(e.target.value, setFuel)}
                />
                <select onChange={(e) => setFuelUnit(e.target.value)}>
                  <option value="l">Litros</option>
                  <option value="gal">Galões</option>
                </select>
              </InputGroup>
            </Field>

            <Field>
              <label><FiZap /> Média de consumo</label>
              <InputGroup>
                <input
                  type="text"
                  readOnly // Campo automático
                  placeholder="0.00"
                  value={consumption}
                />
                <div className="unit-label">{distanceUnit}/{fuelUnit}</div>
              </InputGroup>
            </Field>

            <Field>
              <label><FiDollarSign /> Preço por {fuelUnit === "l" ? "Litro" : "Galão"}</label>
              <input
                type="text"
                placeholder="0.00"
                value={price}
                onChange={(e) => handleNumberInput(e.target.value, setPrice)}
                onBlur={formatPriceOnBlur} // Formata ao clicar fora
              />
            </Field>
          </Grid>

          <ResultSection>
            <div className="total-box">
              <span>Custo Total Estimado</span>
              <h2>R$ {total}</h2>
            </div>
            <ResetButton onClick={clearFields}>Limpar Campos</ResetButton>
          </ResultSection>
        </MainCard>
      </Content>
    </Container>
  );
}

const Container = styled.div`
  width: 100%;
  box-sizing: border-box; 
  padding: 40px 20px 60px; 
  background: radial-gradient(circle at top left, #1a1a1a, #080808);
  color: #fff;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  overflow-x: hidden; 
`;

const Content = styled.div`
  width: 100%;
  max-width: 900px;
  margin: 0 auto;
  box-sizing: border-box;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 30px; // Reduzi de 50px para 30px

  h1 {
    font-size: clamp(32px, 5vw, 48px);
    font-weight: 900;
    margin: 10px 0; // Reduzi a margem do título
    span { color: #ff1a1a; }
  }

  p { 
    color: #888; 
    font-size: 16px;
    max-width: 500px;
    margin: 0 auto; // Centraliza o parágrafo
  }
`;

const Badge = styled.span`
  background: rgba(255, 26, 26, 0.1);
  color: #ff1a1a;
  padding: 6px 16px;
  border-radius: 50px;
  font-size: 12px;
  font-weight: 800;
  text-transform: uppercase;
`;

const MainCard = styled.div`
  background: rgba(255, 255, 255, 0.03);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  border-radius: 24px;
  padding: 40px;
  display: grid;
  grid-template-columns: 1fr minmax(200px, 280px);
  gap: 40px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.4);
  box-sizing: border-box;

  @media (max-width: 850px) {
    grid-template-columns: 1fr; /* Coluna única no mobile */
    padding: 20px;
    gap: 30px;
  }
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 25px;
  width: 100%;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;

  label {
    font-size: 13px;
    font-weight: 600;
    color: #888; /* Tom mais sutil para labels */
    display: flex;
    align-items: center;
    gap: 8px;
    text-transform: uppercase;
    letter-spacing: 0.5px;
    
    svg { color: #ff1a1a; font-size: 14px; }
  }

  /* Input solto (Preço) */
  input {
    background: rgba(255, 255, 255, 0.03);
    border: 1px solid rgba(255, 255, 255, 0.1);
    padding: 14px;
    border-radius: 12px;
    color: #fff;
    font-size: 16px;
    transition: 0.3s;

    &:focus {
      outline: none;
      border-color: #ff1a1a;
      background: rgba(255, 255, 255, 0.05);
    }
  }
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  background: rgba(255, 255, 255, 0.03); /* Fundo igual ao input solto */
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 12px;
  overflow: hidden;
  transition: 0.3s;

  &:focus-within {
    border-color: #ff1a1a;
    background: rgba(255, 255, 255, 0.05);
  }

  input {
    border: none !important;
    background: transparent !important;
    flex: 1;
    padding: 14px;
    color: #fff;
    font-size: 16px;
    width: 100%;

    &::placeholder { color: #444; }
  }

  select {
    background: #1a1a1a; /* Fundo escuro sólido para o select */
    color: #fff;
    border: none;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    padding: 0 15px;
    height: 100%;
    font-size: 13px;
    font-weight: 600;
    cursor: pointer;
    appearance: none; /* Remove a seta padrão do sistema */
    outline: none;
    
    /* Adicionando uma setinha customizada via background (opcional) */
    background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='none' stroke='%23ff1a1a' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E");
    background-repeat: no-repeat;
    background-position: right 8px center;
    background-size: 16px;
    padding-right: 30px;

    &:hover {
      background-color: #222;
    }
  }

  .unit-label {
    background: rgba(255, 255, 255, 0.05);
    color: #ff1a1a;
    font-weight: 700;
    font-size: 11px;
    height: 100%;
    display: flex;
    align-items: center;
    padding: 0 15px;
    border-left: 1px solid rgba(255, 255, 255, 0.1);
    text-transform: uppercase;
  }
`;

const ResultSection = styled.div`
  background: linear-gradient(145deg, #1a1a1a, #111);
  border-radius: 20px;
  padding: 30px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.05);
  .total-box { margin-bottom: 25px; span { font-size: 12px; color: #888; text-transform: uppercase; } h2 { font-size: 38px; color: #ff1a1a; font-weight: 900; } }
`;

const ResetButton = styled.button`
  background: transparent;
  color: #888;
  border: 1px solid #333;
  padding: 10px;
  border-radius: 8px;
  cursor: pointer;
  &:hover { border-color: #ff1a1a; color: #fff; }
`;